import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowRight, ArrowUpRight, Briefcase, CalendarClock, Clock, Inbox, LayoutGrid, Plug, TrendingUp, Users, Wrench } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { AppIcon } from "../components/AppIcon";
import { AppTile } from "../components/AppTile";
import { Alert, Badge, Card, CardHeader, cx, EmptyState, Select, Skeleton } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { formatMetric, relativeTime } from "../lib/format";
import { useMe } from "../lib/session";
import type { InboxItem, KpiPanel, LauncherApp } from "../lib/types";

const PRIORITY_TONE = { urgent: "danger", high: "warning", normal: "accent", low: "neutral" } as const;
const PANEL_ICON: Record<string, typeof Users> = { staffing: Users, service: Wrench, sales: TrendingUp };

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

function NeedsAttention({ apps }: { apps: LauncherApp[] }) {
  const [applicationId, setApp] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("active");
  const inbox = useQuery({
    queryKey: ["inbox", applicationId, priority, status],
    queryFn: () => api.get<{ items: InboxItem[] }>("/api/v1/dashboard/inbox", { applicationId, priority, status }),
    refetchInterval: 60_000,
  });
  const items = inbox.data?.items ?? [];
  const filtered = Boolean(applicationId || priority || status !== "active");

  return (
    <Card className="flex min-h-[380px] flex-col" aria-labelledby="zone-a">
      <CardHeader
        icon={<Inbox className="size-5" />}
        title={<span id="zone-a">Needs your attention</span>}
        description="Approvals and actions from your connected applications."
        actions={items.length > 0 && !filtered ? <Badge tone="accent">{items.length} open</Badge> : undefined}
      />
      <div className="flex flex-wrap gap-2 border-b border-line px-5 py-3">
        <Select aria-label="Filter by application" value={applicationId} onChange={(e) => setApp(e.target.value)} className="h-8 w-auto min-w-36 text-[13px]">
          <option value="">All applications</option>
          {apps.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <Select aria-label="Filter by priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="h-8 w-auto text-[13px]">
          <option value="">Any priority</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </Select>
        <Select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 w-auto text-[13px]">
          <option value="active">Open & in progress</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </Select>
      </div>
      <div className="flex-1">
        {inbox.isLoading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        ) : inbox.isError ? (
          <div className="p-5">
            <Alert tone="danger">{errorMessage(inbox.error)}</Alert>
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Inbox className="size-5" />}
            title={filtered ? "Nothing matches these filters" : apps.length ? "You're all caught up" : "No connected applications yet"}
            description={
              filtered
                ? "Try a different application, priority or status."
                : apps.length
                  ? "When a connected application needs your approval or action, it will appear here."
                  : "Once an administrator grants you access to applications that publish actions to Galeway, your approvals and tasks will appear here."
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {items.map((item) => (
              <li key={item.id}>
                <a
                  href={item.actionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex gap-3.5 px-5 py-3.5 transition-colors hover:bg-surface-2"
                >
                  <AppIcon icon={item.applicationIcon} size="sm" className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className="truncate text-[14px] font-medium text-fg">{item.title}</p>
                      <Badge tone={PRIORITY_TONE[item.priority]}>{item.priority}</Badge>
                      {item.overdue && (
                        <Badge tone="danger">
                          <AlertCircle className="size-3" aria-hidden /> Overdue
                        </Badge>
                      )}
                    </div>
                    {item.description && <p className="mt-0.5 line-clamp-1 text-[12.5px] text-muted">{item.description}</p>}
                    <p className="mt-1 flex flex-wrap items-center gap-x-3 text-[12px] text-subtle">
                      <span>{item.applicationName}</span>
                      {item.dueAt && (
                        <span className={cx("inline-flex items-center gap-1", item.overdue && "text-danger")}>
                          <CalendarClock className="size-3" aria-hidden /> Due {relativeTime(item.dueAt)}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <Clock className="size-3" aria-hidden /> {relativeTime(item.createdAt)}
                      </span>
                      {!item.assignedToMe && <span>Assigned to your role</span>}
                    </p>
                  </div>
                  <ArrowUpRight className="mt-1 size-4 shrink-0 text-subtle group-hover:text-accent" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function KpiPanelCard({ panel }: { panel: KpiPanel }) {
  const Icon = PANEL_ICON[panel.category] ?? Briefcase;
  const connected = panel.metrics.length > 0;
  const newest = connected ? panel.metrics.reduce((a, m) => (m.asOf > a ? m.asOf : a), panel.metrics[0]!.asOf) : null;
  return (
    <Card as="article" className="p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-[13.5px] font-semibold text-fg">
          <Icon className="size-4 text-accent" aria-hidden /> {panel.title}
        </h3>
        {connected ? <span className="text-[11.5px] text-subtle">Updated {relativeTime(newest)}</span> : <Badge>Not connected</Badge>}
      </div>
      {connected ? (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
          {panel.metrics.map((m) => {
            const progress = m.target && m.value !== null ? Math.min(100, Math.round((m.value / m.target) * 100)) : null;
            return (
              <div key={`${m.applicationId}-${m.metricKey}`} className="min-w-0">
                <dt className="truncate text-[12px] text-muted" title={`${m.label} · ${m.applicationName}`}>
                  {m.label}
                </dt>
                <dd className="tabular mt-0.5 text-[19px] font-semibold tracking-tight text-fg">{formatMetric(m.value, m.unit, m.currency)}</dd>
                {progress !== null && (
                  <div className="mt-1.5" title={`${progress}% of target (${formatMetric(m.target, m.unit, m.currency)})`}>
                    <div className="h-1.5 overflow-hidden rounded-full bg-surface-3">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="mt-1 text-[11px] text-subtle">
                      {progress}% of {formatMetric(m.target, m.unit, m.currency)} target
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </dl>
      ) : (
        <div className="mt-3">
          <ul className="space-y-1.5">
            {panel.expected.map((label) => (
              <li key={label} className="flex items-center justify-between text-[12.5px]">
                <span className="text-muted">{label}</span>
                <span className="text-subtle" aria-label="No data">
                  —
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-subtle">
            <Plug className="mt-0.5 size-3 shrink-0" aria-hidden />
            Appears when a connected application you can access publishes this data.
          </p>
        </div>
      )}
    </Card>
  );
}

function TodayAtAGlance() {
  const kpis = useQuery({ queryKey: ["kpis"], queryFn: () => api.get<{ panels: KpiPanel[] }>("/api/v1/dashboard/kpis"), refetchInterval: 120_000 });
  return (
    <section aria-labelledby="zone-b">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 id="zone-b" className="text-[15px] font-semibold">
          Today at a glance
        </h2>
        <span className="text-[12px] text-subtle">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
      </div>
      {kpis.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-36 rounded-xl" />
          ))}
        </div>
      ) : kpis.isError ? (
        <Alert tone="danger">{errorMessage(kpis.error)}</Alert>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
          {kpis.data!.panels.map((p) => (
            <KpiPanelCard key={p.category} panel={p} />
          ))}
        </div>
      )}
    </section>
  );
}

export function DashboardPage() {
  const me = useMe();
  const apps = useQuery({ queryKey: ["launcher"], queryFn: () => api.get<{ items: LauncherApp[] }>("/api/v1/dashboard/apps") });
  const list = apps.data?.items ?? [];
  const firstName = me.user.name.split(" ")[0];

  return (
    <div className="space-y-8">
      <div>
        <p className="text-[13px] font-medium text-subtle">{me.company.name}</p>
        <h1 className="mt-0.5 text-[22px] font-semibold tracking-tight sm:text-2xl">
          {greeting()}, {firstName}
        </h1>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <NeedsAttention apps={list} />
        <TodayAtAGlance />
      </div>

      <section aria-labelledby="zone-c">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 id="zone-c" className="text-[15px] font-semibold">
            Your apps
          </h2>
          {list.length > 0 && (
            <Link to="/apps" className="inline-flex items-center gap-1 text-[13px] font-medium text-accent hover:underline">
              View all <ArrowRight className="size-3.5" aria-hidden />
            </Link>
          )}
        </div>
        {apps.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[132px] rounded-xl" />
            ))}
          </div>
        ) : apps.isError ? (
          <Alert tone="danger">{errorMessage(apps.error)}</Alert>
        ) : list.length === 0 ? (
          <Card>
            <EmptyState
              icon={<LayoutGrid className="size-5" />}
              title="No applications yet"
              description="Your administrator hasn't granted you access to any applications. Once they do, they'll appear here for one-click sign-in."
            />
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {list.slice(0, 8).map((a) => (
              <AppTile key={a.id} app={a} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
